// i18n translation helper functions.
// Most of these functions are exported solely for the
// purposes of testing. The only functions you should
// ever call on are t() and nt().
// t(string, substitutions): translate singular
// Example:
// t('narwhal') => 'narwhal';
// Example with substitutions:
// t(
// 'my %(appearance)s narwhal',
// {
// appearance: 'lovely',
// },
// ) => 'my lovely narwhal';
// nt(singular, plural, count, substitutions): translate plural
// Example:
// nt(
// 'narwhal',
// 'narwhals',
// 2,
// ) => 'narwhals';
// Example with substitutions:
// nt(
// '%(count)s %(appearance)s narwhal',
// '%(count)s %(appearance)s narwhals',
// 2,
// {
// appearance: 'delectable',
// count: 2,
// },
// ) => '2 delectable narwhals';

import Jed from 'jed';

/* Internal Jed instance, to be created by initLocale(). */
let _jed;

// Returns a locale string in Django format,
// so that we can locate PO files.
// For example, zh-cn => zh_CN.
export const getDjangoLocale = (locale) => {
  const [left, right] = locale.split('-');
  return left + (right ? `_${right.toUpperCase()}` : '');
};

// Returns a Jed instance containing translations
// for the passed locale.
export const getJedInstance = (translations) => {
  return new Jed({
    domain: 'javascript',
    missing_key_callback: () => {},
    locale_data: {
      javascript: translations,
    },
  });
};

// Translate singular. Aliased as t() below.
// Always use t(), gettext is internal.
export const gettext = (input, substitutions = {}) => {
  if (!_jed) {
    return input || '';
  }
  return _jed.sprintf(_jed.ngettext(input), substitutions);
};

// Returns the available translations for the passed locale,
// as a JS object.
// Webpack uses `po-catalog-loader` to parse PO files to JSON objects.
// PO files are located in `luciddg-server/translations/[locale].po`.
// Each JSON object is then outputted as a Webpack JS bundle to
// `modules/django/static-cache/js/translations/[locale].js`.
// Confusingly, to use a translation bundle you have to
// require the original PO file. Obliquely, its JS equivalent
// will then be returned. Hence the require() for a PO file
// that you see in this function. This is just what
// po-catalog-loader expects.
// Also, we need to wrap the require() in a try/catch block
// so that we don't blow up tests, as Jest doesn't offer a
// way for us to consume the PO files. We return a non-explosive
// default object where the require() fails. Again, the
// defaults help with tests.
export const getTranslations = (djangoLocale) => {
  try {
    /* eslint-disable import/no-dynamic-require */
    return require(
      `buildingos-translations/${djangoLocale}/LC_MESSAGES/javascript.po`,
    );
    /* eslint-enable import/no-dynamic-require */
  } catch (error) {
    return {
      '': {
        domain: 'messages',
        lang: djangoLocale,
        plural_forms: 'nplurals=2; plural=(n != 1);',
      },
    };
  }
};

const pluralsMap = {
  // nplurals=2; plural=(n != 1); de, en, nb_NO, es, en_GB
  // nplurals=2; plural=(n > 1); fr_FR, fr_CA
  // nplurals=1; plural=0; zh, ja_JP
  en: 'nplurals=2; plural=(n != 1);',
  de: 'nplurals=2; plural=(n != 1);',
  nb_NO: 'nplurals=2; plural=(n != 1);',
  es: 'nplurals=2; plural=(n != 1);',
  en_GB: 'nplurals=2; plural=(n != 1);',
  fr_FR: 'nplurals=2; plural=(n > 1);',
  fr_CA: 'nplurals=2; plural=(n > 1);',
  zh: 'nplurals=1; plural=0;',
  ja_JP: 'nplurals=1; plural=0;',
};

const getPlurals = (djangoLocale) => {
  // Plurals is not returned by `po-catalog-loader`
  let plural_forms = pluralsMap[djangoLocale];
  if (plural_forms === undefined) {
    plural_forms = 'nplurals=2; plural=(n != 1);';
  }
  return plural_forms;
};

// Initialization function that creates a new Jed
// instance, containing translations for the passed
// locale. Translation may then commence.
export const initLocale = (locale = 'en') => {
  const djangoLocale = getDjangoLocale(locale);
  const translations = getTranslations(djangoLocale);
  translations[''].plural_forms = getPlurals(djangoLocale);
  translations[''].lang = djangoLocale;
  _jed = getJedInstance(translations);
};

// Translate with counts. Aliased as nt() below.
// Always use nt(), ngettext is internal.
export const ngettext = (singular, plural, count = 0, substitutions = {}) => {
  if (!_jed) {
    return '';
  }
  return _jed.sprintf(_jed.ngettext(singular, plural, count), substitutions);
};

// Convenient shorthand for ngettext.
// Translate strings with counts.
export const nt = ngettext;

// Convenient shorthand for gettext.
// Translate strings with singular objects.
export const t = gettext;

// FIXME
// Reaching out to globals on the window from inside
// a generic util is not a safe pattern, but this
// util presents a special case. Translations must
// be initialized before other code initializes and
// tries to perform translations. This is the
// easiest way to ensure that translation is ready.
// We will try to find a cleaner way in later iterations.
if (
  window &&
  window.settings &&
  window.settings.user &&
  window.settings.user.language
) {
  initLocale(window.settings.user.language);
}
